<template>
  <v-form class="pa-6">
    <h3 class="text-left mb-1 text-primary">Pruebas</h3>
    <p class="text-caption mb-4">
      Corre de punta a punta el flujo manual de credencial + segundo factor + certificado + firma sobre Alison Server,
      una vez con PIN (firma CAdES) y una vez con OTP (firma PKCS1).
    </p>

    <v-alert v-if="!tokensReady" type="warning" density="compact" variant="tonal" class="mb-4">
      Faltan tokens: generá primero el token de Generation y de Usage en Config Manager.
    </v-alert>
    <v-alert v-if="tenantWarning" type="warning" density="compact" variant="tonal" class="mb-4">
      Nota: se configuró tenant {{ tenantWarning }} — esto va a realizar cambios en ese tenant.
    </v-alert>

    <div class="d-flex ga-2 mb-6">
      <v-btn
        size="small"
        color="primary"
        :disabled="!tokensReady || running"
        @click="runTests">
        Ejecutar pruebas >>
      </v-btn>
    </div>

    <v-divider class="my-6"></v-divider>

    <div v-if="steps.length === 0" class="text-caption text-medium-emphasis">
      Ejecutá "Ejecutar pruebas >>" para ver el progreso.
    </div>
    <StepStatus v-for="step in steps" :key="step.key" :step="step"></StepStatus>
  </v-form>
</template>
<script>
import alisonServer from '@/services/alisonServer.js';
import alisonJS from '@/services/alison_js.js';
import { issueCertificateFromCsr, wrapCsrAsPem, wrapAsPkcs7 } from '@/services/certIssuer.js';
import { computeTotp } from '@/services/totp.js';
import StepStatus from './StepStatus.vue';

const PIN_VALUE = '1234';
const PIN_DESCRIPTION = 'PIN de pruebas automáticas';
const SAMPLE_HASH = 'sTOgwOm+474gFj0q0x1iSNspKqbcse4IeiqlDg/HWuI=';
const HASH_ALGO = '2.16.840.1.101.3.4.2.1'; // SHA-256
const SIGN_ALGO = '1.2.840.113549.1.1.1'; // RSA

// Cada track corre el mismo pipeline (credencial -> 2do factor -> certificado
// -> instalar -> listar -> detalle -> autorizar -> firmar), solo cambia el
// segundo factor usado y el formato de firma final.
const TRACKS = [
  { id: 'pin', label: 'PIN', factorLabel: 'PIN' },
  { id: 'otp', label: 'OTP', factorLabel: 'OTP' },
];

export default {
  name: 'AlisonServerTests',
  components: {
    StepStatus,
  },
  props: {
    serverConfig: { type: Object, default: null },
  },
  data: () => ({
    steps: [],
    running: false,
    otpSecrets: {},
  }),
  computed: {
    tokensReady() {
      return Boolean(this.serverConfig?.generationAccessToken) && Boolean(this.serverConfig?.usageAccessToken) && Boolean(this.serverConfig?.seatId);
    },
    tenantWarning() {
      const tenant = this.serverConfig?.tenant;
      if (!tenant) return null;
      return tenant.toLowerCase() === 'acme' ? null : tenant;
    },
  },
  methods: {
    signFormatFor(trackId) {
      const formats = alisonJS.getServerSignFormats();
      return trackId === 'pin' ? formats.CADES : formats.PKCS1;
    },
    trackStepDefs(track) {
      const k = (name) => `${name}-${track.id}`;
      return [
        { key: k('credencial'), label: `Creando credencial (${track.factorLabel})`, status: 'pending' },
        { key: k('factor'), label: track.id === 'pin' ? 'Asignando PIN a credencial' : 'Generando y confirmando OTP', status: 'pending' },
        {
          key: k('certificado'), label: `Creando certificado (${track.factorLabel})`, status: 'pending', children: [
            { key: k('csr'), label: 'Generando CSR', status: 'pending' },
            { key: k('selfsign'), label: 'Autofirmando certificado', status: 'pending' },
          ]
        },
        { key: k('asignar'), label: `Asignando certificado (${track.factorLabel})`, status: 'pending' },
        { key: k('instalar'), label: `Instalando certificado (${track.factorLabel})`, status: 'pending' },
        { key: k('listar'), label: `Listando certificados (${track.factorLabel})`, status: 'pending' },
        { key: k('detalle'), label: `Detalle certificado (${track.factorLabel})`, status: 'pending' },
        {
          key: k('autorizar'), label: `Autorizar credencial (${track.factorLabel})`, status: 'pending', children: [
            { key: k('valida'), label: 'Credenciales válidas', status: 'pending' },
            { key: k('invalida'), label: 'Credenciales inválidas', status: 'pending' },
          ]
        },
        { key: k('firmar'), label: `Firmar hash (${track.factorLabel} · ${this.signFormatFor(track.id)})`, status: 'pending' },
      ];
    },
    buildInitialSteps() {
      return TRACKS.flatMap(track => this.trackStepDefs(track));
    },
    findStep(key) {
      for (const step of this.steps) {
        if (step.key === key) return step;
        if (step.children) {
          const child = step.children.find(c => c.key === key);
          if (child) return child;
        }
      }
      return null;
    },
    updateStep(key, patch) {
      const step = this.findStep(key);
      if (step) Object.assign(step, patch);
    },
    setRequest(key, method, url, body) {
      this.updateStep(key, { requestMethod: method, requestUrl: url, request: body });
    },
    // El código TOTP vale por una ventana de 30s; si entre calcularlo y que el
    // server lo valide se cruza el borde de la ventana (latencia de red, reloj
    // del server desalineado, etc.) el server responde "invalid_otp" con un
    // código que en realidad era válido. Reintenta con ventanas vecinas
    // (±30s, ±60s) antes de darse por vencido, y solo para ese error puntual.
    async withOtpRetry(secret, attempt) {
      const offsetsSeconds = [0, -30, 30, -60, 60];
      let lastError;
      for (const offsetSeconds of offsetsSeconds) {
        const code = await computeTotp(secret, { at: Date.now() + offsetSeconds * 1000 });
        try {
          return await attempt(code);
        } catch (e) {
          lastError = e;
          const data = e?.response?.data;
          const isInvalidOtp = data?.error === 'invalid_otp' || data?.code === 23256;
          if (!isInvalidOtp) throw e;
        }
      }
      throw lastError;
    },
    classifyError(e) {
      const status = e?.response?.status;
      if (status === 400 || status === 401 || status === 403) return 'credenciales invalidas';
      return 'error inesperado';
    },
    responseOf(e) {
      return e?.response?.data ?? e?.message ?? String(e);
    },
    failStep(key, e) {
      this.updateStep(key, {
        status: 'error',
        message: this.classifyError(e),
        response: this.responseOf(e),
      });
    },
    blockStep(key, message) {
      const step = this.findStep(key);
      if (!step) return;
      step.status = 'blocked';
      step.message = message || 'No ejecutado: paso anterior falló';
      if (step.children) {
        step.children.forEach(child => {
          child.status = 'blocked';
          child.message = message || 'No ejecutado: paso anterior falló';
        });
      }
    },
    blockTrackFrom(order, fromIndex, message) {
      for (let i = fromIndex; i < order.length; i++) {
        this.blockStep(order[i], message);
      }
    },
    async runTests() {
      if (!this.tokensReady || this.running) return;
      this.running = true;
      this.steps = this.buildInitialSteps();
      this.otpSecrets = {};

      alisonServer.changeServerURL(this.serverConfig.serverURL);
      alisonServer.changeTenant(this.serverConfig.tenant);
      alisonServer.generationClient.defaults.headers.common['Authorization'] = `Bearer ${this.serverConfig.generationAccessToken}`;
      alisonServer.generationClient.defaults.headers.common['Content-Type'] = 'application/json';
      alisonServer.usageClient.defaults.headers.common['Authorization'] = `Bearer ${this.serverConfig.usageAccessToken}`;
      alisonServer.usageClient.defaults.headers.common['Content-Type'] = 'application/json';

      for (const track of TRACKS) {
        await this.runTrack(track);
      }

      this.running = false;
    },
    async runTrack(track) {
      const seatId = this.serverConfig.seatId;
      const baseURL = this.serverConfig.serverURL;
      const k = (name) => `${name}-${track.id}`;
      const order = ['credencial', 'factor', 'certificado', 'asignar', 'instalar', 'listar', 'detalle', 'autorizar', 'firmar'].map(k);

      // 1. Creando credencial
      this.updateStep(k('credencial'), { status: 'loading' });
      const credentialData = {
        multisign: '15',
        pin: track.id === 'pin',
        otp: track.id === 'otp',
        sms: false,
        email: false,
        webauthn: false,
      };
      this.setRequest(k('credencial'), 'POST', `${baseURL}/api/v1/seats/${seatId}/credentials`, credentialData);
      let credentialId;
      try {
        credentialId = await alisonServer.createCredential(seatId, credentialData);
        this.updateStep(k('credencial'), { status: 'success', response: { id: credentialId } });
      } catch (e) {
        this.failStep(k('credencial'), e);
        this.blockTrackFrom(order, 1);
        return;
      }

      // 2. Asignando el segundo factor (PIN o OTP)
      this.updateStep(k('factor'), { status: 'loading' });
      if (track.id === 'pin') {
        const body = { pin: PIN_VALUE, description: PIN_DESCRIPTION };
        this.setRequest(k('factor'), 'POST', `${baseURL}/api/v1/seats/${seatId}/credentials/${credentialId}/second_factors`, body);
        try {
          const response = await alisonServer.addPin(PIN_VALUE, PIN_DESCRIPTION, seatId, credentialId);
          this.updateStep(k('factor'), { status: 'success', response });
        } catch (e) {
          this.failStep(k('factor'), e);
          this.blockTrackFrom(order, 2);
          return;
        }
      } else {
        this.setRequest(k('factor'), 'POST', `${baseURL}/api/v1/seats/${seatId}/credentials/${credentialId}/second_factors`, { type: 'OTP' });
        let secret;
        try {
          const genResponse = await alisonServer.generateQR(seatId, credentialId);
          secret = genResponse.secret;
          this.otpSecrets[track.id] = secret;
          this.updateStep(k('factor'), { label: `Generando y confirmando OTP (secret: ${secret})`, response: genResponse });
        } catch (e) {
          this.failStep(k('factor'), e);
          this.blockTrackFrom(order, 2);
          return;
        }
        try {
          const confirmResponse = await this.withOtpRetry(secret, code => alisonServer.verifyQRCode(code, seatId, credentialId));
          this.updateStep(k('factor'), { status: 'success', response: confirmResponse });
        } catch (e) {
          this.failStep(k('factor'), e);
          this.blockTrackFrom(order, 2);
          return;
        }
      }

      // 3. Creando certificado (CSR en el server + autofirma local)
      this.updateStep(k('certificado'), { status: 'loading' });
      this.updateStep(k('csr'), { status: 'loading' });
      this.setRequest(k('csr'), 'POST', `${baseURL}/api/v1/seats/${seatId}/credentials/${credentialId}/key_pair`, {
        csrSignatureAlgorithm: '1.2.840.113549.1.1.11',
        key: { algo: '1.2.840.113549.1.1.1', len: 2048 },
      });
      let csrPem;
      try {
        const response = await alisonServer.generateCSR(seatId, credentialId);
        csrPem = wrapCsrAsPem(response.csr);
        this.updateStep(k('csr'), { status: 'success', response });
      } catch (e) {
        this.failStep(k('csr'), e);
        this.updateStep(k('certificado'), { status: 'error', message: 'Falló la generación de CSR' });
        this.blockStep(k('selfsign'));
        this.blockTrackFrom(order, 3);
        return;
      }

      this.updateStep(k('selfsign'), { status: 'loading' });
      let certBody;
      try {
        certBody = issueCertificateFromCsr(csrPem);
        this.updateStep(k('selfsign'), { status: 'success', response: { certificate: certBody } });
        this.updateStep(k('certificado'), { status: 'success' });
      } catch (e) {
        this.updateStep(k('selfsign'), { status: 'error', message: 'error inesperado', response: this.responseOf(e) });
        this.updateStep(k('certificado'), { status: 'error', message: 'Falló la firma local del certificado' });
        this.blockTrackFrom(order, 3);
        return;
      }

      // 4. Asignando certificado (armar el PKCS7, local, sin request)
      this.updateStep(k('asignar'), { status: 'loading' });
      const pkcs7 = wrapAsPkcs7(certBody);
      this.updateStep(k('asignar'), { status: 'success', response: { pkcs7 } });

      // 5. Instalando certificado
      this.updateStep(k('instalar'), { status: 'loading' });
      this.setRequest(k('instalar'), 'POST', `${baseURL}/api/v1/seats/${seatId}/credentials/${credentialId}/certificate`, { pkcs7 });
      try {
        const response = await alisonServer.installCertificate(pkcs7, seatId, credentialId);
        this.updateStep(k('instalar'), { status: 'success', response });
      } catch (e) {
        this.failStep(k('instalar'), e);
        this.blockTrackFrom(order, 5);
        return;
      }

      // 6. Listando certificados
      this.updateStep(k('listar'), { status: 'loading' });
      this.setRequest(k('listar'), 'POST', `${baseURL}/csc/v1/credentials/list`, null);
      try {
        const response = await alisonServer.listCredentials();
        const list = Array.isArray(response) ? response : (response?.credentialIDs ?? []);
        const found = list.some(item => (typeof item === 'string' ? item : item?.id) === credentialId);
        if (found) {
          this.updateStep(k('listar'), { status: 'success', response });
        } else {
          this.updateStep(k('listar'), {
            status: 'warning',
            message: 'La credencial recién creada no aparece en el listado (puede ser demora de propagación)',
            response,
          });
        }
      } catch (e) {
        this.failStep(k('listar'), e);
        this.blockTrackFrom(order, 6);
        return;
      }

      // 7. Detalle certificado
      this.updateStep(k('detalle'), { status: 'loading' });
      this.setRequest(k('detalle'), 'POST', `${baseURL}/csc/v1/credentials/info`, {
        credentialId,
        certificates: 'none',
        certInfo: true,
        authInfo: true,
      });
      try {
        const response = await alisonServer.certificateInfo(credentialId);
        this.updateStep(k('detalle'), { status: 'success', response });
      } catch (e) {
        this.failStep(k('detalle'), e);
        this.blockTrackFrom(order, 7);
        return;
      }

      // 8. Autorizar credencial (válida + inválida, independientes entre sí)
      let sad = null;

      this.updateStep(k('valida'), { status: 'loading' });
      try {
        let response;
        if (track.id === 'pin') {
          const validaBody = { credentialId, numSignatures: 1, hash: [SAMPLE_HASH], PIN: PIN_VALUE };
          this.setRequest(k('valida'), 'POST', `${baseURL}/csc/v1/credentials/authorize`, validaBody);
          response = await alisonServer.authorize(validaBody);
        } else {
          response = await this.withOtpRetry(this.otpSecrets[track.id], code => {
            const validaBody = { credentialId, numSignatures: 1, hash: [SAMPLE_HASH], OTP: code };
            this.setRequest(k('valida'), 'POST', `${baseURL}/csc/v1/credentials/authorize`, validaBody);
            return alisonServer.authorize(validaBody);
          });
        }
        sad = response.SAD;
        this.updateStep(k('valida'), { status: 'success', response });
      } catch (e) {
        this.failStep(k('valida'), e);
      }

      this.updateStep(k('invalida'), { status: 'loading' });
      const invalidaBody = { credentialId, numSignatures: 1, hash: [SAMPLE_HASH] };
      if (track.id === 'pin') invalidaBody.PIN = PIN_VALUE + 'X';
      else invalidaBody.OTP = '000000';
      this.setRequest(k('invalida'), 'POST', `${baseURL}/csc/v1/credentials/authorize`, invalidaBody);
      try {
        const response = await alisonServer.authorize(invalidaBody);
        this.updateStep(k('invalida'), {
          status: 'error',
          message: `Se autorizó con ${track.factorLabel} inválido (se esperaba que fallara)`,
          response,
        });
      } catch (e) {
        const classification = this.classifyError(e);
        this.updateStep(k('invalida'), {
          status: classification === 'credenciales invalidas' ? 'success' : 'error',
          message: classification === 'credenciales invalidas' ? 'Falló como se esperaba (credenciales inválidas)' : classification,
          response: this.responseOf(e),
        });
      }

      const validaStep = this.findStep(k('valida'));
      const invalidaStep = this.findStep(k('invalida'));
      this.updateStep(k('autorizar'), {
        status: (validaStep.status === 'success' && invalidaStep.status === 'success') ? 'success' : 'error',
      });

      if (validaStep.status !== 'success') {
        this.blockStep(k('firmar'), 'No ejecutado: falta SAD de autorización válida');
        return;
      }

      // 9. Firmar hash, con el formato fijo asignado al track (PIN -> CAdES, OTP -> PKCS1)
      this.updateStep(k('firmar'), { status: 'loading' });
      const signBody = {
        credentialId,
        SAD: sad,
        hash: [SAMPLE_HASH],
        hashAlgo: HASH_ALGO,
        signAlgo: SIGN_ALGO,
        signFormat: this.signFormatFor(track.id),
      };
      this.setRequest(k('firmar'), 'POST', `${baseURL}/csc/v1/signatures/signHash`, signBody);
      try {
        const response = await alisonServer.signHash(signBody);
        this.updateStep(k('firmar'), { status: 'success', response });
      } catch (e) {
        this.failStep(k('firmar'), e);
      }
    },
  },
}
</script>
